/**
 * useSocialMeta — injecte dynamiquement les balises Open Graph + Twitter Card
 * dans le <head> pour chaque page. Efficace pour :
 *   - les vues real-users (Chrome, Safari, Edge) et les partages depuis un onglet
 *   - Facebook (crawler exécute une partie du JS depuis 2020)
 *   - Google (indexation dynamique)
 *
 * ⚠️ Les crawlers "no-JS" (WhatsApp, LinkedIn, Twitterbot, iMessage) NE voient
 * PAS les tags injectés par ce hook — ils lisent uniquement le HTML statique.
 * Pour ces crawlers, on utilise en parallèle l'endpoint backend
 * `GET /api/og/property/{id}` qui renvoie du HTML pré-rendu avec les OG tags.
 *
 * Pattern : le hook remplace les tags existants (par property) ou les insère
 * s'ils n'existent pas encore, puis les restaure sur unmount pour éviter les
 * fuites entre pages (ex: SPA navigation Home → PropertyDetails → Home).
 */
import { useEffect } from 'react';

const OG_TAGS = [
  ['property', 'og:title'],
  ['property', 'og:description'],
  ['property', 'og:image'],
  ['property', 'og:image:alt'],
  ['property', 'og:url'],
  ['property', 'og:type'],
];

const TWITTER_TAGS = [
  ['name', 'twitter:title'],
  ['name', 'twitter:description'],
  ['name', 'twitter:image'],
  ['name', 'twitter:image:alt'],
  ['name', 'twitter:card'],
];

const setMeta = (attr, key, value) => {
  if (!value) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const previous = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(value));
  return { attr, key, previous, wasCreated: previous === null };
};

/**
 * @param {object} opts
 * @param {string} opts.title       — Titre montré sur les aperçus sociaux
 * @param {string} opts.description — Description courte (<200 chars idéal)
 * @param {string} opts.image       — URL absolue de l'image (1200×630 idéal)
 * @param {string} opts.imageAlt    — Texte alternatif de l'image (a11y)
 * @param {string} opts.url         — URL canonique (partagée sur les réseaux)
 * @param {string} opts.type        — "website" (default) | "article" | "product"
 */
const useSocialMeta = ({
  title,
  description,
  image,
  imageAlt,
  url,
  type = 'website',
} = {}) => {
  useEffect(() => {
    if (!title && !description && !image) return undefined;

    // Default to the clean canonical URL (pathname only) — never leak
    // query strings like ?utm_source=whatsapp into og:url (SEO-003).
    const canonical = url || (window.location.origin + window.location.pathname);
    const values = {
      'og:title': title,
      'og:description': description,
      'og:image': image,
      'og:image:alt': imageAlt || title,
      'og:url': canonical,
      'og:type': type,
      'twitter:card': image ? 'summary_large_image' : 'summary',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image,
      'twitter:image:alt': imageAlt || title,
    };

    const applied = [];
    // Standard <meta name="description"> — SEO-004 : Google SERP snippet
    // reads this exact tag, so it must be per-page not the generic
    // index.html default. Kept alongside og:description / twitter:description
    // (which have subtly different length rules on each platform).
    if (description) {
      const res = setMeta('name', 'description', description);
      if (res) applied.push(res);
    }
    [...OG_TAGS, ...TWITTER_TAGS].forEach(([attr, key]) => {
      const res = setMeta(attr, key, values[key]);
      if (res) applied.push(res);
    });

    return () => {
      // Restore previous state so navigation to another page doesn't leak
      // property-specific OG tags onto e.g. the Home hero.
      applied.forEach(({ attr, key, previous, wasCreated }) => {
        const el = document.head.querySelector(`meta[${attr}="${key}"]`);
        if (!el) return;
        if (wasCreated) {
          el.remove();
        } else if (previous !== null) {
          el.setAttribute('content', previous);
        }
      });
    };
  }, [title, description, image, imageAlt, url, type]);
};

export default useSocialMeta;
